import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2, ShoppingCart, ChefHat, Package, User, ArrowRight, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo profiles with pre-loaded credentials
const DEMO_PROFILES = [
  {
    id: 'admin',
    name: 'Administrador',
    icon: Building2,
    email: 'admin@lunchroll.com',
    password: 'admin123',
    description: 'Dashboard completo, analytics, AI agents, gestión de usuarios',
    color: 'bg-blue-500',
    redirect: '/admin-dashboard'
  },
  {
    id: 'compras',
    name: 'Compras',
    icon: ShoppingCart,
    email: 'compras@lunchroll.com',
    password: 'compras123',
    description: 'Gestión de inventario, proveedores, órdenes de compra',
    color: 'bg-amber-500',
    redirect: '/compras'
  },
  {
    id: 'produccion',
    name: 'Producción',
    icon: ChefHat,
    email: 'produccion@lunchroll.com',
    password: 'prod123',
    description: 'KDS, plan de producción, rutas de entrega',
    color: 'bg-orange-500',
    redirect: '/produccion'
  },
  {
    id: 'proveedor',
    name: 'Proveedor',
    icon: Package,
    email: 'proveedor@ejemplo.com',
    password: 'prov123',
    description: 'Ver órdenes de compra, cotizar, facturar',
    color: 'bg-teal-500',
    redirect: '/proveedor'
  },
  {
    id: 'empresa',
    name: 'Cliente Empresa',
    icon: Building2,
    email: 'empresa@kapital.com',
    password: 'empresa123',
    description: 'Gestionar empleados, ver reportes, facturación',
    color: 'bg-indigo-500',
    redirect: '/empresa'
  },
  {
    id: 'cliente',
    name: 'Comprador',
    icon: User,
    email: 'comprador@kapital.com',
    password: 'cliente123',
    description: 'Hacer pedidos, ver menú, historial de compras',
    color: 'bg-emerald-500',
    redirect: '/cliente'
  }
];

const LoginPage = () => {
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'cliente_comprador',
    company: '',
    whatsapp: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roles`);
      const data = await res.json();
      setRoles(data.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const handleProfileClick = (profile) => {
    setSelectedProfile(profile.id);
    setFormData(prev => ({
      ...prev,
      email: profile.email,
      password: profile.password
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate before submit
    if (!formData.email || !formData.password) {
      setError('Por favor ingresa email y contraseña');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Error en autenticación');
      }

      if (data.user && data.session_token) {
        localStorage.setItem('session_token', data.session_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        login(data.user);
      } else if (!isLogin) {
        setIsLogin(true);
        setError('');
        alert('¡Registro exitoso! Ahora puedes iniciar sesión.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    // Clear profile selection if user types
    if (e.target.name === 'email' || e.target.name === 'password') {
      setSelectedProfile(null);
    }
  };

  const getRoleIcon = (roleId) => {
    const icons = {
      admin: '👔',
      compras: '🛒',
      produccion: '🏭',
      proveedor: '📦',
      cliente_empresa: '🏢',
      cliente_comprador: '🛍️'
    };
    return icons[roleId] || '👤';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-stone-50 to-amber-50 flex">
      {/* Left Side - Demo Profiles */}
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-900 p-12 flex-col justify-center relative overflow-hidden">
        {/* Grain texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
        
        <div className="relative z-10 max-w-md">
          <h2 className="text-3xl font-bold text-white mb-3">Prueba la plataforma</h2>
          <p className="text-emerald-200 mb-8">
            Haz clic en un perfil para cargar las credenciales automáticamente y explorar sus funciones.
          </p>

          <div className="space-y-3">
            {DEMO_PROFILES.map((profile) => {
              const Icon = profile.icon;
              const isSelected = selectedProfile === profile.id;
              
              return (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => handleProfileClick(profile)}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-200 border ${
                    isSelected 
                      ? 'bg-white/15 border-white/30 scale-[1.02]' 
                      : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                  }`}
                  data-testid={`demo-profile-${profile.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${profile.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{profile.name}</h3>
                      <p className="text-emerald-300 text-sm">{profile.description}</p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                        <ArrowRight className="w-4 h-4 text-emerald-900" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-8 p-4 bg-emerald-800/50 rounded-xl border border-emerald-700/50">
            <p className="text-emerald-200 text-sm">
              <strong className="text-white">Tip:</strong> Cada perfil tiene acceso a diferentes funcionalidades. 
              El <strong>Administrador</strong> puede ver todo, incluyendo los AI Agents.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Lunch & Roll" className="h-16 mx-auto mb-2" />
            <p className="text-stone-500 mt-2">Alimentación inteligente para oficinas</p>
          </div>

          {/* Mobile Demo Profiles */}
          <div className="lg:hidden mb-6">
            <p className="text-sm text-stone-500 mb-3 font-medium">Perfiles de prueba:</p>
            <div className="flex flex-wrap gap-2">
              {DEMO_PROFILES.map((profile) => {
                const Icon = profile.icon;
                const isSelected = selectedProfile === profile.id;
                
                return (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => handleProfileClick(profile)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                      isSelected 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                    data-testid={`mobile-demo-${profile.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {profile.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Tabs */}
            <div className="flex mb-6 bg-stone-100 rounded-xl p-1">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  isLogin ? 'bg-emerald-600 text-white shadow-sm' : 'text-stone-600 hover:text-emerald-600'
                }`}
                data-testid="login-tab"
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  !isLogin ? 'bg-emerald-600 text-white shadow-sm' : 'text-stone-600 hover:text-emerald-600'
                }`}
                data-testid="register-tab"
              >
                Registrarse
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100" data-testid="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name (only for register) */}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="Tu nombre"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  placeholder="tu@email.com"
                  required
                  data-testid="email-input"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all pr-12"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Role Selection (only for register) */}
              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Tipo de usuario
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {roles.map(role => (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, role: role.id }))}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            formData.role === role.id
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-stone-200 hover:border-emerald-300'
                          }`}
                          data-testid={`role-${role.id}`}
                        >
                          <span className="text-xl">{getRoleIcon(role.id)}</span>
                          <div className="text-sm font-medium text-stone-800 mt-1">
                            {role.name_es}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Empresa (opcional)
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Nombre de tu empresa"
                      data-testid="company-input"
                    />
                  </div>
                </>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-lg shadow-emerald-600/20"
                data-testid="submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'
                )}
              </button>
            </form>

            {/* Selected Profile Info */}
            {isLogin && selectedProfile && (
              <div className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-emerald-700 text-sm">
                  <strong>Perfil cargado:</strong> {DEMO_PROFILES.find(p => p.id === selectedProfile)?.name}
                </p>
              </div>
            )}

            {/* Quick Access Demo Buttons - Always Visible */}
            {isLogin && (
              <div className="mt-6 pt-6 border-t border-stone-200">
                <p className="text-sm text-stone-500 mb-3 text-center font-medium">Acceso rápido demo:</p>
                <div className="grid grid-cols-3 gap-2">
                  {DEMO_PROFILES.map((profile) => {
                    const Icon = profile.icon;
                    const isSelected = selectedProfile === profile.id;
                    
                    return (
                      <button
                        key={profile.id}
                        type="button"
                        onClick={() => handleProfileClick(profile)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs transition-all border-2 ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                            : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-emerald-300 hover:bg-emerald-50'
                        }`}
                        data-testid={`quick-demo-${profile.id}`}
                      >
                        <div className={`w-8 h-8 ${profile.color} rounded-lg flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium truncate w-full text-center">{profile.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-stone-400 text-sm mt-6">
            © 2026 Lunch & Roll - Comida Saludable
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
